'use client';

import Image from '@/components/Image';
import { env } from '@/utils/env';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { StyleHotProductRawCategoryTab } from '../../types';
import './StyleCategorySwiperWap.css';

interface StyleCategorySwiperWapProps {
  tabs?: StyleHotProductRawCategoryTab[];
  activeTabIndex?: number;
  onActiveTabChange?: (tabIndex: number) => void;
  onUserNavigate?: (swiperEl: HTMLElement) => void;
}

interface StyleCategorySlide {
  key: string;
  title: string;
  imageSrc: string;
  tabIndex: number;
}

type SwiperSlideElement = HTMLElement & {
  progress?: number;
};

type SwiperWithAllowClick = SwiperType & {
  allowClick?: boolean;
};

interface StyleCategoryWapSlot {
  progress: number;
  x: number;
  y: number;
  width: number;
  height: number;
  opacity: number;
  path: StyleCategoryWapPathCommand[];
}

interface StyleCategoryWapPathCommand {
  command: 'M' | 'L' | 'C' | 'Z';
  points: Array<[number, number]>;
}

const REPEAT_COUNT = 7;
const MIDDLE_REPEAT_INDEX = Math.floor(REPEAT_COUNT / 2);
const DEFAULT_ACTIVE_OFFSET = 2;
const OUTSIDE_SLOT_VISIBILITY_OFFSET = 0.28;
const DESIGN_CENTER_CARD_RADIUS = 6;
const STYLE_CATEGORY_WAP_SWIPER_SPEED = 0;
const SIDE_CARD_IMAGE_ROTATE_Y = 28;
const DESIGN_CARD_ASPECT_RATIO = 168 / 251;

const CENTER_SLOT_LEFT_PATH: StyleCategoryWapPathCommand[] = [
  { command: 'M', points: [[1, DESIGN_CENTER_CARD_RADIUS / 251]] },
  {
    command: 'C',
    points: [
      [1, 2.686 / 251],
      [165.314 / 168, 0],
      [1 - DESIGN_CENTER_CARD_RADIUS / 168, 0],
    ],
  },
  { command: 'L', points: [[DESIGN_CENTER_CARD_RADIUS / 168, 0]] },
  {
    command: 'C',
    points: [
      [2.686 / 168, 0],
      [0, 2.686 / 251],
      [0, DESIGN_CENTER_CARD_RADIUS / 251],
    ],
  },
  { command: 'L', points: [[0, 1 - DESIGN_CENTER_CARD_RADIUS / 251]] },
  {
    command: 'C',
    points: [
      [0, 248.314 / 251],
      [2.686 / 168, 1],
      [DESIGN_CENTER_CARD_RADIUS / 168, 1],
    ],
  },
  { command: 'L', points: [[1 - DESIGN_CENTER_CARD_RADIUS / 168, 1]] },
  {
    command: 'C',
    points: [
      [165.314 / 168, 1],
      [1, 248.314 / 251],
      [1, 1 - DESIGN_CENTER_CARD_RADIUS / 251],
    ],
  },
  { command: 'L', points: [[1, DESIGN_CENTER_CARD_RADIUS / 251]] },
  { command: 'Z', points: [] },
];

const CENTER_SLOT_RIGHT_PATH: StyleCategoryWapPathCommand[] = [
  { command: 'M', points: [[0, DESIGN_CENTER_CARD_RADIUS / 251]] },
  {
    command: 'C',
    points: [
      [0, 2.686 / 251],
      [2.686 / 168, 0],
      [DESIGN_CENTER_CARD_RADIUS / 168, 0],
    ],
  },
  { command: 'L', points: [[1 - DESIGN_CENTER_CARD_RADIUS / 168, 0]] },
  {
    command: 'C',
    points: [
      [165.314 / 168, 0],
      [1, 2.686 / 251],
      [1, DESIGN_CENTER_CARD_RADIUS / 251],
    ],
  },
  { command: 'L', points: [[1, 1 - DESIGN_CENTER_CARD_RADIUS / 251]] },
  {
    command: 'C',
    points: [
      [1, 248.314 / 251],
      [165.314 / 168, 1],
      [1 - DESIGN_CENTER_CARD_RADIUS / 168, 1],
    ],
  },
  { command: 'L', points: [[DESIGN_CENTER_CARD_RADIUS / 168, 1]] },
  {
    command: 'C',
    points: [
      [2.686 / 168, 1],
      [0, 248.314 / 251],
      [0, 1 - DESIGN_CENTER_CARD_RADIUS / 251],
    ],
  },
  { command: 'L', points: [[0, DESIGN_CENTER_CARD_RADIUS / 251]] },
  { command: 'Z', points: [] },
];

// Coordinates are normalized from the Figma mobile SVG masks in a 375x256 stage.
const STYLE_CATEGORY_WAP_SLOTS: StyleCategoryWapSlot[] = [
  {
    progress: -1,
    x: -31.5,
    y: 0,
    width: 127,
    height: 252,
    opacity: 0.6,
    path: [
      { command: 'M', points: [[125.5944 / 127, 22.3953 / 252]] },
      {
        command: 'C',
        points: [
          [125.5695 / 127, 18.7347 / 252],
          [122.8224 / 127, 15.6472 / 252],
          [119.1444 / 127, 15.1461 / 252],
        ],
      },
      { command: 'L', points: [[8.4962 / 127, 0.0706638 / 252]] },
      {
        command: 'C',
        points: [
          [4.0048 / 127, -0.541265 / 252],
          [0, 2.8991 / 252],
          [0, 7.36934 / 252],
        ],
      },
      { command: 'L', points: [[0, 244.631 / 252]] },
      {
        command: 'C',
        points: [
          [0, 249.109 / 252],
          [4.0186 / 127, 252.552 / 252],
          [8.5164 / 127, 251.927 / 252],
        ],
      },
      { command: 'L', points: [[120.57 / 127, 236.352 / 252]] },
      {
        command: 'C',
        points: [
          [124.2767 / 127, 235.837 / 252],
          [127.0249 / 127, 232.698 / 252],
          [126.9998 / 127, 229.007 / 252],
        ],
      },
      { command: 'L', points: [[125.5944 / 127, 22.3953 / 252]] },
      { command: 'Z', points: [] },
    ],
  },
  {
    progress: 0,
    x: 103.5,
    y: 1,
    width: 168,
    height: 251,
    opacity: 1,
    path: CENTER_SLOT_LEFT_PATH,
  },
  {
    progress: 1,
    x: 279.5,
    y: 0,
    width: 127,
    height: 252,
    opacity: 0.6,
    path: [
      { command: 'M', points: [[1.40559 / 127, 22.3953 / 252]] },
      {
        command: 'C',
        points: [
          [1.43049 / 127, 18.7347 / 252],
          [4.17756 / 127, 15.6472 / 252],
          [7.85558 / 127, 15.1461 / 252],
        ],
      },
      { command: 'L', points: [[118.504 / 127, 0.0706638 / 252]] },
      {
        command: 'C',
        points: [
          [122.995 / 127, -0.541265 / 252],
          [1, 2.8991 / 252],
          [1, 7.36934 / 252],
        ],
      },
      { command: 'L', points: [[1, 244.631 / 252]] },
      {
        command: 'C',
        points: [
          [1, 249.109 / 252],
          [122.981 / 127, 252.552 / 252],
          [118.484 / 127, 251.927 / 252],
        ],
      },
      { command: 'L', points: [[6.42997 / 127, 236.352 / 252]] },
      {
        command: 'C',
        points: [
          [2.72331 / 127, 235.837 / 252],
          [-0.0249335 / 127, 232.698 / 252],
          [0.000170599 / 127, 229.007 / 252],
        ],
      },
      { command: 'L', points: [[1.40559 / 127, 22.3953 / 252]] },
      { command: 'Z', points: [] },
    ],
  },
];

function lerp(from: number, to: number, progress: number): number {
  return from + (to - from) * progress;
}

function getSlotByProgress(progress: number): StyleCategoryWapSlot {
  if (progress <= STYLE_CATEGORY_WAP_SLOTS[0].progress) {
    return STYLE_CATEGORY_WAP_SLOTS[0];
  }

  const lastSlot = STYLE_CATEGORY_WAP_SLOTS[STYLE_CATEGORY_WAP_SLOTS.length - 1];

  if (progress >= lastSlot.progress) {
    return lastSlot;
  }

  const previousSlotIndex = STYLE_CATEGORY_WAP_SLOTS.findIndex(
    (slot, index) =>
      index < STYLE_CATEGORY_WAP_SLOTS.length - 1 &&
      progress >= slot.progress &&
      progress <= STYLE_CATEGORY_WAP_SLOTS[index + 1].progress,
  );
  const fromSlot = STYLE_CATEGORY_WAP_SLOTS[previousSlotIndex];
  const toSlot = STYLE_CATEGORY_WAP_SLOTS[previousSlotIndex + 1];
  const slotProgress = (progress - fromSlot.progress) / (toSlot.progress - fromSlot.progress);
  const fromPath =
    fromSlot.progress === 0 && toSlot.progress > 0 ? CENTER_SLOT_RIGHT_PATH : fromSlot.path;
  const toPath =
    toSlot.progress === 0 && fromSlot.progress > 0 ? CENTER_SLOT_RIGHT_PATH : toSlot.path;

  return {
    progress,
    x: lerp(fromSlot.x, toSlot.x, slotProgress),
    y: lerp(fromSlot.y, toSlot.y, slotProgress),
    width: lerp(fromSlot.width, toSlot.width, slotProgress),
    height: lerp(fromSlot.height, toSlot.height, slotProgress),
    opacity: lerp(fromSlot.opacity, toSlot.opacity, slotProgress),
    path: fromPath.map((command, commandIndex) => ({
      command: command.command,
      points: command.points.map(([x, y], pointIndex) => {
        const [toX, toY] = toPath[commandIndex].points[pointIndex];

        return [lerp(x, toX, slotProgress), lerp(y, toY, slotProgress)];
      }),
    })),
  };
}

function toClipPath(slot: StyleCategoryWapSlot): string {
  const path = slot.path
    .map(({ command, points }) => {
      if (command === 'Z') {
        return command;
      }

      const values = points
        .map(([x, y]) => `${(x * slot.width).toFixed(3)} ${(y * slot.height).toFixed(3)}`)
        .join(' ');

      return `${command}${values}`;
    })
    .join(' ');

  return `path("${path}")`;
}

function getStageEl(swiper: SwiperType): HTMLElement | null {
  return (
    swiper.el
      .closest('.style-hot-product-category-shell-wap')
      ?.querySelector<HTMLElement>('.style-hot-product-category-stage-wap') || null
  );
}

function isActiveSlide(swiper: SwiperType, slideEl: Element): boolean {
  const slideIndex = Number(slideEl.getAttribute('data-style-slide-index'));

  return slideIndex === swiper.activeIndex || slideEl.classList.contains('swiper-slide-active');
}

function resolveAssetUrl(path?: string | null): string {
  if (!path) {
    return '';
  }

  if (/^(https?:)?\/\//.test(path) || path.startsWith('blob:')) {
    return path;
  }

  if (path.startsWith('/admin/')) {
    return `${env.NEXT_PUBLIC_S3_URL}${path}`;
  }

  return path;
}

function normalizeSlides(tabs?: StyleHotProductRawCategoryTab[]): StyleCategorySlide[] {
  return (tabs || []).flatMap((tab, index) => {
    const imageSrc = resolveAssetUrl(tab.wap_tab_image || tab.pc_tab_image);

    if (!imageSrc) {
      return [];
    }

    const title = tab.tab_title || tab.tab_name || tab.title || '';

    return [
      {
        key: String(tab.uid || tab.id || `${title}-${index}`),
        title,
        imageSrc,
        tabIndex: index,
      },
    ];
  });
}

function updateWapCarouselStyles(swiper?: SwiperType | null) {
  if (!swiper || swiper.destroyed || !swiper.slides) {
    return;
  }

  const stageEl = getStageEl(swiper);

  if (!stageEl) {
    return;
  }

  Array.from(swiper.slides).forEach((slideEl) => {
    const card = stageEl.querySelector<HTMLElement>(
      `.style-hot-product-category-card-wap[data-style-slide-index="${slideEl.getAttribute(
        'data-style-slide-index',
      )}"]`,
    );

    if (!card) {
      return;
    }

    const slideProgress = -((slideEl as SwiperSlideElement).progress || 0);
    const progress = Math.max(Math.min(slideProgress, 1), -1);
    const absProgress = Math.abs(progress);
    const imagePerspectiveProgress = Math.min(absProgress, 1);
    const imageRotateY = -Math.sign(progress) * SIDE_CARD_IMAGE_ROTATE_Y;
    const isOutside =
      slideProgress < STYLE_CATEGORY_WAP_SLOTS[0].progress - OUTSIDE_SLOT_VISIBILITY_OFFSET ||
      slideProgress >
        STYLE_CATEGORY_WAP_SLOTS[STYLE_CATEGORY_WAP_SLOTS.length - 1].progress +
          OUTSIDE_SLOT_VISIBILITY_OFFSET;
    const slot = getSlotByProgress(progress);
    const imageWidth = slot.height * DESIGN_CARD_ASPECT_RATIO;

    card.style.setProperty('--style-card-wap-x', `${slot.x.toFixed(3)}px`);
    card.style.setProperty('--style-card-wap-y', `${slot.y.toFixed(3)}px`);
    card.style.setProperty('--style-card-wap-width', `${slot.width.toFixed(3)}px`);
    card.style.setProperty('--style-card-wap-height', `${slot.height.toFixed(3)}px`);
    card.style.setProperty('--style-card-wap-clip-path', toClipPath(slot));
    card.style.setProperty(
      '--style-card-wap-image-rotate-y',
      `${(imageRotateY * imagePerspectiveProgress).toFixed(3)}deg`,
    );
    card.style.setProperty('--style-card-wap-image-width', `${imageWidth.toFixed(3)}px`);
    card.style.opacity = isOutside ? '0' : String(slot.opacity);
    card.style.zIndex = String(20 - Math.round(absProgress * 5));

    if (isActiveSlide(swiper, slideEl)) {
      card.style.setProperty('--style-card-wap-transition-duration', '0ms');
    }
  });
}

function updateWapCarouselTransition(swiper: SwiperType, duration = 0) {
  const stageEl = getStageEl(swiper);

  if (!stageEl) {
    return;
  }

  Array.from(swiper.slides).forEach((slideEl) => {
    const card = stageEl.querySelector<HTMLElement>(
      `.style-hot-product-category-card-wap[data-style-slide-index="${slideEl.getAttribute(
        'data-style-slide-index',
      )}"]`,
    );

    if (!card) {
      return;
    }

    card.style.setProperty(
      '--style-card-wap-transition-duration',
      isActiveSlide(swiper, slideEl) ? '0ms' : `${duration}ms`,
    );
  });
}

function StyleCategoryCardWap({
  slide,
  index,
  slideIndex,
}: {
  slide: StyleCategorySlide;
  index: number;
  slideIndex: number;
}) {
  return (
    <div className="style-hot-product-category-card-wap" data-style-slide-index={slideIndex}>
      <div className="style-hot-product-category-card-image-wap">
        <Image
          src={slide.imageSrc}
          alt={slide.title || `style category ${index + 1}`}
          fill
          sizes="168px"
          className="block h-full w-full object-cover object-center"
        />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[rgba(0,0,0,0)] from-[68%] to-[rgba(0,0,0,0.42)]" />
    </div>
  );
}

export default function StyleCategorySwiperWap({
  tabs,
  activeTabIndex,
  onActiveTabChange,
  onUserNavigate,
}: StyleCategorySwiperWapProps) {
  const swiperRef = useRef<SwiperType | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const clickNavigatePendingRef = useRef(false);
  const slideNavigateStartedAtRef = useRef(0);
  const slides = useMemo(() => normalizeSlides(tabs), [tabs]);
  const loopSlides = useMemo(
    () =>
      Array.from({ length: REPEAT_COUNT }, (_, repeatIndex) =>
        slides.map((slide) => ({
          ...slide,
          renderKey: `${repeatIndex}-${slide.key}`,
        })),
      ).flat(),
    [slides],
  );
  const activeSlideOffset = slides.findIndex((slide) => slide.tabIndex === activeTabIndex);
  const initialActiveOffset =
    activeSlideOffset >= 0
      ? activeSlideOffset
      : slides.length > DEFAULT_ACTIVE_OFFSET
        ? DEFAULT_ACTIVE_OFFSET
        : 0;
  const initialSlide = slides.length
    ? MIDDLE_REPEAT_INDEX * slides.length + initialActiveOffset
    : 0;

  const resetToMiddleRepeat = useCallback(
    (swiper: SwiperType) => {
      if (slides.length <= 1) {
        return;
      }

      const activeIndex = swiper.activeIndex;
      const realOffset = activeIndex % slides.length;
      const middleIndex = MIDDLE_REPEAT_INDEX * slides.length + realOffset;
      const lowerBoundary = slides.length * 2;
      const upperBoundary = slides.length * (REPEAT_COUNT - 2);

      if (activeIndex < lowerBoundary || activeIndex >= upperBoundary) {
        swiper.slideTo(middleIndex, 0, false);
      }
    },
    [slides.length],
  );

  const handleSlideChangeTransitionEnd = useCallback(
    (swiper: SwiperType) => {
      resetToMiddleRepeat(swiper);
      updateWapCarouselTransition(swiper, 0);
      updateWapCarouselStyles(swiper);
    },
    [resetToMiddleRepeat],
  );

  const handleSwiper = useCallback(
    (swiper: SwiperType) => {
      swiperRef.current = swiper;
      requestAnimationFrame(() => {
        if (swiper.destroyed) {
          return;
        }

        updateWapCarouselTransition(swiper, 0);
        updateWapCarouselStyles(swiper);
        const realOffset = slides.length ? swiper.activeIndex % slides.length : 0;
        onActiveTabChange?.(slides[realOffset]?.tabIndex || 0);
      });
    },
    [onActiveTabChange, slides],
  );

  const handleActiveSlideChange = useCallback(
    (swiper: SwiperType) => {
      if (slides.length === 0) {
        return;
      }

      const realOffset = swiper.activeIndex % slides.length;
      onActiveTabChange?.(slides[realOffset]?.tabIndex || 0);

      const isUserNavigate =
        clickNavigatePendingRef.current || Date.now() - slideNavigateStartedAtRef.current < 1200;

      clickNavigatePendingRef.current = false;
      slideNavigateStartedAtRef.current = 0;

      if (isUserNavigate && shellRef.current) {
        onUserNavigate?.(shellRef.current);
      }
    },
    [onActiveTabChange, onUserNavigate, slides],
  );

  const handleCategoryClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const swiper = swiperRef.current;
    const allowClick = (swiper as SwiperWithAllowClick | null)?.allowClick;

    if (!swiper || swiper.destroyed || allowClick === false) {
      return;
    }

    const cards = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>('.style-hot-product-category-card-wap'),
    );
    const hitCard = cards
      .filter((card) => Number(card.style.opacity || '1') > 0)
      .sort((firstCard, secondCard) => {
        const firstZIndex = Number(firstCard.style.zIndex || '0');
        const secondZIndex = Number(secondCard.style.zIndex || '0');

        return secondZIndex - firstZIndex;
      })
      .find((card) => {
        const rect = card.getBoundingClientRect();

        return (
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom
        );
      });

    if (!hitCard) {
      return;
    }

    const slideIndex = Number(hitCard.dataset.styleSlideIndex);

    if (!Number.isFinite(slideIndex) || slideIndex === swiper.activeIndex) {
      return;
    }

    clickNavigatePendingRef.current = true;
    swiper.slideTo(slideIndex, STYLE_CATEGORY_WAP_SWIPER_SPEED);
  }, []);

  useEffect(() => {
    const swiper = swiperRef.current;

    if (!swiper || !slides.length || activeTabIndex === undefined) {
      return;
    }

    const targetOffset = slides.findIndex((slide) => slide.tabIndex === activeTabIndex);

    if (targetOffset < 0) {
      return;
    }

    const currentOffset = swiper.activeIndex % slides.length;

    if (currentOffset === targetOffset) {
      return;
    }

    const repeatIndex = Math.floor(swiper.activeIndex / slides.length);
    swiper.slideTo(repeatIndex * slides.length + targetOffset, STYLE_CATEGORY_WAP_SWIPER_SPEED);
  }, [activeTabIndex, slides]);

  if (slides.length === 0) {
    return null;
  }

  return (
    <div
      ref={shellRef}
      className="style-hot-product-category-shell-wap relative h-[256px] w-full overflow-hidden"
      onClick={handleCategoryClick}
    >
      <div className="style-hot-product-category-stage-wap" aria-hidden="true">
        {loopSlides.map((slide, index) => (
          <StyleCategoryCardWap
            key={`stage-${slide.renderKey}`}
            slide={slide}
            index={index}
            slideIndex={index}
          />
        ))}
      </div>
      <Swiper
        key={`${slides.length}-${initialSlide}`}
        className="style-hot-product-category-swiper-wap"
        slidesPerView="auto"
        centeredSlides
        initialSlide={initialSlide}
        spaceBetween={8}
        speed={STYLE_CATEGORY_WAP_SWIPER_SPEED}
        watchSlidesProgress
        onSwiper={handleSwiper}
        onProgress={updateWapCarouselStyles}
        onSetTranslate={updateWapCarouselStyles}
        onSetTransition={updateWapCarouselTransition}
        onTouchStart={(swiper) => {
          updateWapCarouselTransition(swiper, 0);
        }}
        onSliderMove={(swiper) => {
          slideNavigateStartedAtRef.current = Date.now();
          updateWapCarouselStyles(swiper);
        }}
        onSlideChange={handleActiveSlideChange}
        onSlideChangeTransitionEnd={handleSlideChangeTransitionEnd}
      >
        {loopSlides.map((slide, index) => (
          <SwiperSlide key={slide.renderKey} aria-hidden="true" data-style-slide-index={index}>
            <span className="sr-only">{slide.title}</span>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
